/**
 * Canvas scenes for the founder block, in the same white-on-black language as
 * the bottleneck: thin routes, particles, glows and rings, and the founder as
 * a ring that fills when something lands on them. Coordinates are normalised
 * to the stage (0–1). Each scene reports its beats through `emit` so the HTML
 * cards and counters can follow without re-rendering every frame.
 */

type Vec = { x: number; y: number };
type Ring = { x: number; y: number; from: number; to: number; age: number; duration: number; width: number };

export type SceneEngine = {
  resize: () => void;
  step: (dt: number) => void;
  draw: (time: number) => void;
  settle: () => void;
};

export type SceneEvent =
  | { type: "reset" }
  | { type: "bounce"; count: number; from: "product" | "engineering" }
  | { type: "escalate" }
  | { type: "decay" }
  | { type: "echo"; index: number };

function seeded(seed: number) {
  let value = seed >>> 0;

  return () => {
    value = (value + 0x6d2b79f5) >>> 0;
    let mixed = Math.imul(value ^ (value >>> 15), 1 | value);
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), 61 | mixed);
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296;
  };
}

function quad(a: Vec, c: Vec, b: Vec, t: number): Vec {
  const u = 1 - t;
  return {
    x: u * u * a.x + 2 * u * t * c.x + t * t * b.x,
    y: u * u * a.y + 2 * u * t * c.y + t * t * b.y,
  };
}

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const easeOutCubic = (t: number) => 1 - Math.pow(1 - clamp01(t), 3);

// Layout size, not the on-screen size, so a stage scaled by a transform still draws crisply.
function setupCanvas(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  return { width, height };
}

function ringAt(point: Vec, from: number, to: number, duration = 0.7, width = 1.25): Ring {
  return { x: point.x, y: point.y, from, to, age: 0, duration, width };
}

function stepRings(rings: Ring[], dt: number) {
  for (const ring of rings) ring.age += dt;
  return rings.filter((ring) => ring.age < ring.duration);
}

function drawRings(ctx: CanvasRenderingContext2D, rings: Ring[]) {
  for (const ring of rings) {
    if (ring.age < 0) continue;
    const progress = ring.age / ring.duration;
    ctx.beginPath();
    ctx.arc(ring.x, ring.y, ring.from + (ring.to - ring.from) * easeOutCubic(progress), 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(255,255,255,${0.7 * Math.pow(1 - progress, 1.6)})`;
    ctx.lineWidth = ring.width;
    ctx.stroke();
  }
}

function glow(ctx: CanvasRenderingContext2D, point: Vec, radius: number, alpha: number) {
  if (alpha <= 0) return;
  const gradient = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, radius);
  gradient.addColorStop(0, `rgba(255,255,255,${alpha})`);
  gradient.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
  ctx.fill();
}

function dot(ctx: CanvasRenderingContext2D, point: Vec, radius: number, alpha = 1) {
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(255,255,255,${alpha})`;
  ctx.fill();
}

/** The founder, as on the bottleneck: a capacity ring with a disc that fills as things land. */
function drawFounder(ctx: CanvasRenderingContext2D, point: Vec, fill: number, flash: number) {
  const radius = 11;
  glow(ctx, point, radius * (2 + 1.6 * fill) + flash * 32, 0.12 + 0.3 * fill + 0.45 * flash);
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
  ctx.strokeStyle = `rgba(255,255,255,${0.4 + 0.45 * fill})`;
  ctx.lineWidth = 1;
  ctx.stroke();
  dot(ctx, point, radius * (0.4 + 0.6 * fill));
}

function dottedLine(ctx: CanvasRenderingContext2D, from: Vec, to: Vec, alpha: number) {
  ctx.setLineDash([2, 5]);
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.setLineDash([]);
}

/** A tapered comet tail through recent positions, newest first. */
function drawTrail(ctx: CanvasRenderingContext2D, trail: Vec[], headWidth: number) {
  ctx.lineCap = "round";
  for (let index = 0; index < trail.length - 1; index += 1) {
    const fade = 1 - index / (trail.length - 1);
    ctx.beginPath();
    ctx.moveTo(trail[index].x, trail[index].y);
    ctx.lineTo(trail[index + 1].x, trail[index + 1].y);
    ctx.strokeStyle = `rgba(255,255,255,${0.95 * fade * fade})`;
    ctx.lineWidth = Math.max(0.4, headWidth * fade);
    ctx.stroke();
  }
  ctx.lineCap = "butt";
}

/* ------------------------------------------------------------------------ */
/* 03 · Ping-pong: one ticket loops between two functions, then escalates.  */
/* ------------------------------------------------------------------------ */

export const PINGPONG = {
  product: { x: 0.2, y: 0.42 },
  engineering: { x: 0.8, y: 0.42 },
  founder: { x: 0.5, y: 0.86 },
  upper: { x: 0.5, y: 0.03 },
  lower: { x: 0.5, y: 0.81 },
  /** Where each side's quote card ends; a dotted stem joins it to the cluster below. */
  quoteBottom: 0.22,
} as const;

/** Each pass is a little quicker than the last: the handoff gets more impatient. */
const BOUNCE_TIMES = [0.9, 0.8, 0.72, 0.66, 0.6];
const ESCALATE_TIME = 0.85;
/** Rounds the loop to one 5.5 s story step. */
const HOLD_TIME = 0.97;

type Side = "product" | "engineering";

export function createPingPongEngine(
  canvas: HTMLCanvasElement,
  emit: (event: SceneEvent) => void,
): SceneEngine | null {
  const context = canvas.getContext("2d");

  if (!context) return null;

  const ctx = context;
  const random = seeded(41);
  let width = 0;
  let height = 0;
  let phase: "bounce" | "escalate" | "hold" = "bounce";
  let from: Side = "product";
  let bounce = 0;
  let t = 0;
  let trail: Vec[] = [];
  let rings: Ring[] = [];
  let flash = 0;
  let landed = 0;
  let started = false;
  const impact = { product: 0, engineering: 0 };
  /** How worn each half of the loop looks: it brightens with every pass. */
  const wear = { upper: 0, lower: 0 };

  const team = Array.from({ length: 14 }, (_, index) => ({
    side: index < 7 ? ("product" as const) : ("engineering" as const),
    angle: random() * Math.PI * 2,
    radius: 0.55 + random() * 0.45,
    speed: (0.25 + random() * 0.35) * (random() > 0.5 ? 1 : -1),
  }));

  const px = (point: Vec) => ({ x: point.x * width, y: point.y * height });
  const other = (side: Side): Side => (side === "product" ? "engineering" : "product");
  const arcOf = (side: Side) => (side === "product" ? "upper" : "lower");
  const clusterRadius = () => Math.min(width, height) * 0.06;

  const bouncePoint = (side: Side, progress: number) =>
    px(quad(PINGPONG[side], PINGPONG[arcOf(side)], PINGPONG[other(side)], progress));

  const escalatePoint = (side: Side, progress: number) =>
    px(quad(PINGPONG[side], { x: PINGPONG[side].x, y: PINGPONG.founder.y }, PINGPONG.founder, progress));

  const ticketPosition = (): Vec => {
    if (phase === "bounce") return bouncePoint(from, easeInOut(t));
    if (phase === "escalate") return escalatePoint(from, easeInOut(t));
    return px(PINGPONG.founder);
  };

  const strokePart = (point: (progress: number) => Vec, upTo: number, alpha: number) => {
    if (upTo <= 0) return;
    ctx.beginPath();
    for (let index = 0; index <= 32; index += 1) {
      const p = point((index / 32) * upTo);
      if (index === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    }
    ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
    ctx.lineWidth = 1;
    ctx.stroke();
  };

  return {
    resize() {
      ({ width, height } = setupCanvas(canvas, ctx));
    },

    step(dt) {
      if (!started) {
        started = true;
        emit({ type: "bounce", count: 1, from });
      }

      const duration = phase === "bounce" ? BOUNCE_TIMES[bounce] : phase === "escalate" ? ESCALATE_TIME : HOLD_TIME;
      t += dt / duration;

      if (t >= 1) {
        t = 0;

        if (phase === "bounce") {
          wear[arcOf(from)] = Math.min(3, wear[arcOf(from)] + 1);
          from = other(from);
          impact[from] = 1;
          rings.push(ringAt(px(PINGPONG[from]), 6, 40 + bounce * 4));
          bounce += 1;

          if (bounce >= BOUNCE_TIMES.length) {
            phase = "escalate";
          } else {
            emit({ type: "bounce", count: bounce + 1, from });
          }
        } else if (phase === "escalate") {
          phase = "hold";
          flash = 1;
          landed = 1;
          rings.push(ringAt(px(PINGPONG.founder), 11, 70, 0.8, 1.5), ringAt(px(PINGPONG.founder), 11, 44, 0.6));
          emit({ type: "escalate" });
        } else {
          phase = "bounce";
          from = "product";
          bounce = 0;
          landed = 0;
          wear.upper = 0;
          wear.lower = 0;
          trail = [];
          emit({ type: "reset" });
          emit({ type: "bounce", count: 1, from });
        }
      }

      for (const member of team) member.angle += member.speed * dt;
      impact.product = Math.max(0, impact.product - dt * 2.4);
      impact.engineering = Math.max(0, impact.engineering - dt * 2.4);
      rings = stepRings(rings, dt);
      flash = Math.max(0, flash - dt * 1.6);

      trail.unshift(ticketPosition());
      trail = trail.slice(0, 16);
    },

    draw() {
      ctx.clearRect(0, 0, width, height);

      const product = px(PINGPONG.product);
      const engineering = px(PINGPONG.engineering);
      const founder = px(PINGPONG.founder);
      const upper = px(PINGPONG.upper);
      const lower = px(PINGPONG.lower);
      const radius = clusterRadius();

      // The loop the ticket keeps travelling, wearing in with every pass.
      ctx.setLineDash([2, 6]);
      ctx.strokeStyle = "rgba(255,255,255,0.18)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(product.x, product.y);
      ctx.quadraticCurveTo(upper.x, upper.y, engineering.x, engineering.y);
      ctx.quadraticCurveTo(lower.x, lower.y, product.x, product.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(product.x, product.y);
      ctx.quadraticCurveTo(product.x, founder.y, founder.x, founder.y);
      ctx.moveTo(engineering.x, engineering.y);
      ctx.quadraticCurveTo(engineering.x, founder.y, founder.x, founder.y);
      ctx.strokeStyle = "rgba(255,255,255,0.1)";
      ctx.stroke();
      ctx.setLineDash([]);

      strokePart((p) => bouncePoint("product", p), 1, 0.07 * wear.upper);
      strokePart((p) => bouncePoint("engineering", p), 1, 0.07 * wear.lower);
      if (phase === "bounce") strokePart((p) => bouncePoint(from, p), easeInOut(t), 0.32);
      if (phase === "escalate") strokePart((p) => escalatePoint(from, p), easeInOut(t), 0.45);
      if (phase === "hold") strokePart((p) => escalatePoint(from, p), 1, 0.3 * (1 - t * 0.5));

      // Stems from each quote card down to its side, on stages wide enough to show the cards.
      if (width >= 640) {
        (["product", "engineering"] as const).forEach((side) => {
          const node = px(PINGPONG[side]);
          const speaking = phase === "bounce" && from === side;
          dottedLine(
            ctx,
            { x: node.x, y: PINGPONG.quoteBottom * height + 6 },
            { x: node.x, y: node.y - radius * 1.7 },
            speaking ? 0.5 : 0.22,
          );
        });
      }

      // Each function as a small cluster of people that flinches when the ticket lands.
      for (const member of team) {
        const centre = member.side === "product" ? product : engineering;
        const spread = radius * member.radius * (1 + 0.7 * impact[member.side]);
        dot(
          ctx,
          {
            x: centre.x + Math.cos(member.angle) * spread,
            y: centre.y + Math.sin(member.angle) * spread * 0.8,
          },
          1.8,
          0.75,
        );
      }
      glow(ctx, product, radius * 1.6, 0.1 + 0.25 * impact.product);
      glow(ctx, engineering, radius * 1.6, 0.1 + 0.25 * impact.engineering);
      dot(ctx, product, 4);
      dot(ctx, engineering, 4);

      drawRings(ctx, rings);
      drawFounder(ctx, founder, landed, flash);

      // The ticket and its tail.
      if (trail.length > 1) drawTrail(ctx, trail, 3.4);
      if (trail[0]) {
        glow(ctx, trail[0], 20, 0.5);
        dot(ctx, trail[0], 3.6);
      }
    },

    settle() {
      phase = "bounce";
      from = "product";
      t = 0.5;
      bounce = 2;
      wear.upper = 1;
      wear.lower = 1;
      trail = [ticketPosition()];
      rings = [];
    },
  };
}

/* ------------------------------------------------------------------------ */
/* 04 · Echo: an agreement is sealed, then comes apart over time. Its pieces */
/* drift along the timeline while the same question returns at every step,  */
/* and over the last mile they are all drawn into the founder.              */
/* ------------------------------------------------------------------------ */

export const ECHO = {
  lineY: 0.74,
  agreement: { x: 0.12, y: 0.45 },
  /** Bottom edge of the agreement card on wide stages, for the stem down to the seal. */
  agreementBottom: 0.24,
  echoes: [0.38, 0.55, 0.72, 0.87],
  /** How high above the timeline each returning question sits (alternating). */
  lift: [0.43, 0.13],
  founder: { x: 0.95, y: 0.74 },
} as const;

const FORM_TIME = 0.7;
/** The playhead from March to today. */
const TRAVEL_TIME = 3.3;
/** The last mile: from today to the founder, while the drifting pieces are drawn in. */
const LAND_TIME = 1;
const ECHO_HOLD = 0.5;
/** Seconds a stem takes to rise from the timeline to its question. */
const STEM_TIME = 0.28;

type Mote = {
  /** Its place on the seal. */
  tx: number;
  ty: number;
  x: number;
  y: number;
  alpha: number;
  /** When it forms (in the form phase) and when it lets go (in the travel phase), 0–1. */
  delay: number;
  release: number;
  loose: boolean;
  /** Once loose, it floats this far behind the playhead and this high above the timeline (px). */
  lag: number;
  lane: number;
  phase: number;
  /** The last mile: where it set off from, when, how long it takes and how high it arcs. */
  from: Vec;
  landDelay: number;
  landTime: number;
  arc: number;
  landed: boolean;
};

export function createEchoEngine(
  canvas: HTMLCanvasElement,
  emit: (event: SceneEvent) => void,
): SceneEngine | null {
  const context = canvas.getContext("2d");

  if (!context) return null;

  const ctx = context;
  const random = seeded(73);
  let width = 0;
  let height = 0;
  let phase: "form" | "travel" | "land" | "hold" = "form";
  let t = 0;
  let echoed = -1;
  let echoAge = [0, 0, 0, 0];
  let rings: Ring[] = [];
  let flash = 0;
  let filled = 0;
  let sealed = 0;
  let motes: Mote[] = [];
  let started = false;
  let settled = false;

  const px = (point: Vec) => ({ x: point.x * width, y: point.y * height });
  const sealRadius = () => Math.min(width, height) * 0.075;

  // The seal: an outer ring of motes and a finer inner ring, like a stamp.
  const scatter = () => {
    const centre = px(ECHO.agreement);
    const radius = sealRadius();
    const outer = 64;
    const inner = 28;

    motes = Array.from({ length: outer + inner }, (_, index) => {
      const onOuter = index < outer;
      const angle = onOuter ? (index / outer) * Math.PI * 2 : ((index - outer) / inner) * Math.PI * 2 + 0.11;
      const ring = onOuter ? 1 : 0.62;
      const from = random() * Math.PI * 2;
      const distance = radius * (1.8 + random() * 1.6);

      return {
        tx: centre.x + Math.cos(angle) * radius * ring,
        ty: centre.y + Math.sin(angle) * radius * ring,
        x: centre.x + Math.cos(from) * distance,
        y: centre.y + Math.sin(from) * distance,
        alpha: 0,
        delay: random() * 0.35,
        release: 0.04 + random() * 0.8,
        loose: false,
        lag: 6 + Math.pow(random(), 0.8) * width * 0.3,
        lane: height * (0.025 + random() * 0.075),
        phase: random() * Math.PI * 2,
        from: { x: 0, y: 0 },
        landDelay: 0,
        landTime: 0.32 + random() * 0.2,
        arc: 6 + random() * 26,
        landed: false,
      };
    });
  };

  const playheadX = () => {
    const startX = ECHO.agreement.x;
    const today = ECHO.echoes[ECHO.echoes.length - 1];
    if (phase === "form") return startX;
    if (phase === "travel") return startX + (today - startX) * t;
    if (phase === "land") return today + (ECHO.founder.x - today) * easeInOut(t);
    return ECHO.founder.x;
  };

  /** Where a loose mote floats: behind the playhead, in a band just above the timeline. */
  const riverAt = (mote: Mote, time: number) => ({
    x: Math.max(px(ECHO.agreement).x, playheadX() * width - mote.lag),
    y: ECHO.lineY * height - mote.lane + Math.sin(time * 1.4 + mote.phase) * 4,
  });

  // The still frame: about half the seal has let go and floats along the timeline.
  const loosenedSeal = () => {
    for (const mote of motes) {
      mote.loose = mote.release < 0.5;
      const spot = mote.loose ? riverAt(mote, mote.phase) : { x: mote.tx, y: mote.ty };
      mote.x = spot.x;
      mote.y = spot.y;
      mote.alpha = mote.loose ? 0.45 : 0.7;
    }
  };

  const land = () => {
    const founder = px(ECHO.founder);
    for (const mote of motes) {
      mote.loose = true;
      mote.from = { x: mote.x, y: mote.y };
      // The nearest pieces arrive first, the stragglers from March last.
      mote.landDelay = (1 - mote.x / founder.x) * 0.45 + random() * 0.08;
    }
  };

  return {
    resize() {
      ({ width, height } = setupCanvas(canvas, ctx));
      scatter();
      if (settled) loosenedSeal();
      else if (phase !== "form") {
        for (const mote of motes) {
          mote.x = mote.tx;
          mote.y = mote.ty;
          mote.alpha = 0.9;
        }
      }
    },

    step(dt) {
      if (!started) {
        started = true;
        emit({ type: "reset" });
      }

      const duration = { form: FORM_TIME, travel: TRAVEL_TIME, land: LAND_TIME, hold: ECHO_HOLD }[phase];
      t += dt / duration;
      const time = performance.now() / 1000;

      if (phase === "form") {
        // Motes gather onto the seal one after another, then it stamps shut.
        for (const mote of motes) {
          const local = clamp01((t - mote.delay) / 0.6);
          const pull = Math.min(1, dt * (4 + 10 * local));
          mote.x += (mote.tx - mote.x) * pull;
          mote.y += (mote.ty - mote.y) * pull;
          mote.alpha = Math.min(0.9, mote.alpha + dt * 2.4 * (local > 0 ? 1 : 0));
        }

        if (t >= 1) {
          phase = "travel";
          t = 0;
          sealed = 1;
          rings.push(ringAt(px(ECHO.agreement), sealRadius(), sealRadius() * 1.6, 0.6));
          emit({ type: "decay" });
        }
      } else if (phase === "travel") {
        // As time passes the seal lets go, mote by mote, and its pieces drift along with time.
        for (const mote of motes) {
          if (!mote.loose && t >= mote.release) mote.loose = true;

          if (mote.loose) {
            const spot = riverAt(mote, time);
            mote.x += (spot.x - mote.x) * Math.min(1, dt * 1.8);
            mote.y += (spot.y - mote.y) * Math.min(1, dt * 1.4);
            mote.alpha += (0.45 - mote.alpha) * Math.min(1, dt * 2);
          }
        }

        const head = playheadX();
        ECHO.echoes.forEach((x, index) => {
          if (index > echoed && head >= x - 0.002) {
            echoed = index;
            echoAge[index] = 0;
            rings.push(ringAt(px({ x, y: ECHO.lineY }), 4, 16 + index * 6));
            emit({ type: "echo", index });
          }
        });

        if (t >= 1) {
          phase = "land";
          t = 0;
          land();
        }
      } else if (phase === "land") {
        // The last mile: every drifting piece is drawn into the founder, who fills with them.
        const founder = px(ECHO.founder);
        const elapsed = t * LAND_TIME;
        let arrived = 0;

        for (const mote of motes) {
          const progress = clamp01((elapsed - mote.landDelay) / mote.landTime);
          const pull = progress * progress * progress;
          mote.x = mote.from.x + (founder.x - mote.from.x) * pull;
          mote.y = mote.from.y + (founder.y - mote.from.y) * pull - Math.sin(Math.PI * progress) * mote.arc;
          mote.alpha = 0.45 + 0.5 * progress;

          if (progress >= 1) {
            arrived += 1;
            if (!mote.landed) mote.landed = true;
          }
        }

        const before = filled;
        filled = arrived / Math.max(1, motes.length);
        if (before < 0.97 && filled >= 0.97) {
          flash = 1;
          rings.push(ringAt(founder, 11, 50, 0.8, 1.5));
        }

        if (t >= 1) {
          phase = "hold";
          t = 0;
          filled = 1;
        }
      } else if (t >= 1) {
        phase = "form";
        t = 0;
        echoed = -1;
        filled = 0;
        sealed = 0;
        scatter();
        emit({ type: "reset" });
      }

      echoAge = echoAge.map((age) => age + dt);
      rings = stepRings(rings, dt);
      flash = Math.max(0, flash - dt * 1.4);
      sealed = Math.max(0, sealed - dt * 1.8);
    },

    draw(time) {
      ctx.clearRect(0, 0, width, height);

      const lineY = ECHO.lineY * height;
      const startX = 0.05 * width;
      const founder = px(ECHO.founder);
      const seal = px(ECHO.agreement);
      const today = ECHO.echoes[ECHO.echoes.length - 1] * width;
      const radius = sealRadius();
      const head = playheadX() * width;

      // The timeline from the agreement to today, brighter where time has already passed.
      ctx.beginPath();
      ctx.moveTo(startX, lineY);
      ctx.lineTo(founder.x, lineY);
      ctx.strokeStyle = "rgba(255,255,255,0.2)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(seal.x, lineY);
      ctx.lineTo(Math.min(head, today), lineY);
      ctx.strokeStyle = "rgba(255,255,255,0.5)";
      ctx.stroke();

      // The last mile lights up as everything crosses it.
      if (head > today) {
        const lastMile = ctx.createLinearGradient(today, lineY, founder.x, lineY);
        lastMile.addColorStop(0, "rgba(255,255,255,0.5)");
        lastMile.addColorStop(1, "rgba(255,255,255,0.95)");
        ctx.strokeStyle = lastMile;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(today, lineY);
        ctx.lineTo(head, lineY);
        ctx.stroke();
        ctx.lineWidth = 1;
      }

      const ticks = [ECHO.agreement.x, ...ECHO.echoes];
      ticks.forEach((x, index) => {
        const reached = index === 0 || index - 1 <= echoed;
        dot(ctx, { x: x * width, y: lineY }, reached ? 3.2 : 2.2, reached ? 1 : 0.35);
      });

      // Stems: card to seal to March, and from each returning question down to its moment.
      if (width >= 640) {
        dottedLine(ctx, { x: seal.x, y: ECHO.agreementBottom * height + 6 }, { x: seal.x, y: seal.y - radius - 8 }, 0.28);
      }
      dottedLine(ctx, { x: seal.x, y: seal.y + radius + 8 }, { x: seal.x, y: lineY - 6 }, 0.28);

      ECHO.echoes.forEach((x, index) => {
        if (index > echoed) return;
        const rise = easeOutCubic(echoAge[index] / STEM_TIME);
        const top = lineY - 6 - (height * ECHO.lift[index % 2] - 6) * rise;
        dottedLine(ctx, { x: x * width, y: lineY - 6 }, { x: x * width, y: top }, 0.3);
      });

      // The playhead, with a soft light where it meets the timeline.
      if (phase !== "hold") {
        const sweep = ctx.createLinearGradient(head, lineY - height * 0.3, head, lineY + 10);
        sweep.addColorStop(0, "rgba(255,255,255,0)");
        sweep.addColorStop(1, "rgba(255,255,255,0.6)");
        ctx.strokeStyle = sweep;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(head, lineY - height * 0.3);
        ctx.lineTo(head, lineY + 10);
        ctx.stroke();
        if (phase !== "form") glow(ctx, { x: head, y: lineY }, 14, 0.45);
      }

      // The seal: a soft light while it holds, then motes letting go and drifting with time.
      const holding = motes.filter((mote) => !mote.loose).length / Math.max(1, motes.length);
      glow(ctx, seal, radius * 1.9, (phase === "form" ? 0.08 * t : 0.1 * holding) + 0.3 * sealed);
      for (const mote of motes) {
        if (mote.landed) continue;
        const jitter = mote.loose ? 0 : (1 - holding) * 1.2;
        dot(
          ctx,
          { x: mote.x + Math.sin(time * 2 + mote.tx) * jitter, y: mote.y + Math.cos(time * 1.7 + mote.ty) * jitter },
          mote.loose ? 1.4 : 1.5,
          mote.alpha,
        );
      }

      drawRings(ctx, rings);
      drawFounder(ctx, founder, phase === "hold" ? 1 : filled, flash);
    },

    settle() {
      settled = true;
      phase = "travel";
      t = 0.6;
      echoed = 1;
      echoAge = [1, 1, 1, 1];
      loosenedSeal();
    },
  };
}
